import { useState, useRef, useCallback, useEffect } from 'react';

export type CaptureMode = 'on_user_turn' | 'every_n_seconds';

export interface ScreenShareConfig {
  enabled: boolean;
  captureMode: CaptureMode;
  intervalSeconds: number;
}

export interface UseScreenShareTabOptions {
  config: ScreenShareConfig;
  onFrame: (base64Jpeg: string) => void;
}

export interface UseScreenShareTabResult {
  isSharing: boolean;
  error: string | null;
  startShare: () => Promise<void>;
  stopShare: () => void;
  /** Capture a frame right now and send it via onFrame. */
  captureNow: () => void;
  /** Capture a frame right now and return the base64 JPEG (or null). Does NOT call onFrame. */
  captureSnapshot: () => string | null;
}

const MAX_DIMENSION = 1280;
const JPEG_QUALITY = 0.7;
const BACKGROUND_REFRESH_MS = 2000;

function captureFrameFromVideo(video: HTMLVideoElement): string | null {
  if (video.videoWidth === 0 || video.videoHeight === 0) return null;

  const canvas = document.createElement('canvas');
  let w = video.videoWidth;
  let h = video.videoHeight;
  if (w > MAX_DIMENSION || h > MAX_DIMENSION) {
    const scale = MAX_DIMENSION / Math.max(w, h);
    w = Math.round(w * scale);
    h = Math.round(h * scale);
  }
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, w, h);
  const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  return dataUrl.split(',')[1] || null;
}

export function useScreenShareTab({ config, onFrame }: UseScreenShareTabOptions): UseScreenShareTabResult {
  const [isSharing, setIsSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onFrameRef = useRef(onFrame);
  const configRef = useRef(config);

  useEffect(() => { onFrameRef.current = onFrame; }, [onFrame]);
  useEffect(() => { configRef.current = config; }, [config]);

  const cleanup = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current = null;
    }
    setIsSharing(false);
  }, []);

  const doCapture = useCallback(() => {
    if (!videoRef.current) return;
    const b64 = captureFrameFromVideo(videoRef.current);
    if (b64) {
      onFrameRef.current(b64);
    }
  }, []);

  const captureSnapshot = useCallback((): string | null => {
    if (!videoRef.current) return null;
    return captureFrameFromVideo(videoRef.current);
  }, []);

  const startPeriodicCapture = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    const mode = configRef.current.captureMode;
    const ms = mode === 'every_n_seconds'
      ? Math.max(1, configRef.current.intervalSeconds) * 1000
      : BACKGROUND_REFRESH_MS;
    intervalRef.current = setInterval(doCapture, ms);
  }, [doCapture]);

  const startShare = useCallback(async () => {
    if (isSharing) return;
    setError(null);

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'browser' } as any,
        audio: false,
      });

      streamRef.current = stream;

      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;
      await video.play();
      videoRef.current = video;

      setIsSharing(true);

      stream.getVideoTracks()[0].addEventListener('ended', () => {
        cleanup();
      });

      startPeriodicCapture();
    } catch (err: any) {
      if (err.name === 'NotAllowedError' || err.name === 'AbortError') {
        setError('Screen sharing was cancelled.');
      } else {
        setError('Failed to start screen sharing: ' + (err.message || err));
      }
      cleanup();
    }
  }, [isSharing, cleanup, startPeriodicCapture]);

  const stopShare = useCallback(() => {
    cleanup();
  }, [cleanup]);

  const captureNow = useCallback(() => {
    doCapture();
  }, [doCapture]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  useEffect(() => {
    if (!isSharing) return;
    startPeriodicCapture();
  }, [isSharing, config.captureMode, config.intervalSeconds, startPeriodicCapture]);

  return { isSharing, error, startShare, stopShare, captureNow, captureSnapshot };
}
